<template>
  <div class="py-3">
    <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:10px;">
      <h4 class="text-primary mb-0" style="font-weight:600;">Transactions</h4>

      <b-dropdown text="Add transaction" variant="primary" right>
        <b-dropdown-item @click="openModal('deposit')">Add deposit</b-dropdown-item>
        <b-dropdown-item @click="openModal('withdrawal')">Add withdrawal</b-dropdown-item>
        <b-dropdown-item @click="openModal('journal')">Add journal entry</b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="row align-items-center mb-3">
      <div class="col-md-4 mb-2">
        <div class="d-flex align-items-center flex-wrap" style="gap:8px;">
          <select class="form-control" v-model="filters.account_id" @change="onAccountChange">
            <option value="">All accounts</option>
            <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.name }}</option>
          </select>
          <div v-if="selectedAccount" class="d-flex align-items-center flex-wrap" style="gap:8px;font-size:13px;">
            <span class="font-weight-bold text-dark">{{ formatCurrency(selectedAccount.calculated_balance) }}</span>
            <span class="badge badge-light">
              {{ reconciliation && !reconciliation.never_reconciled ? 'Last reconciled ' + reconciliation.last_reconciled_on : 'Never reconciled' }}
            </span>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-2" v-if="!reconciliationMode">
        <input
          type="text"
          class="form-control"
          placeholder="Search transactions"
          v-model="filters.search"
          @keyup.enter="fetchTransactions"
        >
      </div>
      <div class="col-md-3 mb-2" v-if="!reconciliationMode">
        <date-range-picker
          :auto-apply="true"
          v-model="dateRange"
          :ranges="false"
          opens="right"
          :append-to-body="true"
          class="w-100"
          @update="onDateChange"
        >
          <template #input="picker">
            <div class="form-control" style="cursor:pointer">
              <i class="bi bi-calendar3 mr-1"></i>
              <span v-if="picker.startDate">{{ fmt(picker.startDate, 'DD MMM') }} – {{ fmt(picker.endDate, 'DD MMM YYYY') }}</span>
              <span v-else class="text-muted">Filter by date</span>
            </div>
          </template>
        </date-range-picker>
      </div>
      <div class="col-md-2 mb-2 d-flex align-items-center justify-content-md-end" style="gap:8px;">
        <span class="text-secondary" style="font-size:13px;white-space:nowrap;"><i class="bi bi-speedometer2 mr-1"></i>Reconciliation</span>
        <div class="ov-switch" :class="{ 'ov-switch--on': reconciliationMode }" @click="reconciliationMode = !reconciliationMode"></div>
      </div>
    </div>

    <div v-if="reconciliationMode && !filters.account_id" class="bg-white border text-center py-5 mb-3" style="border-radius:10px;">
      <p class="text-secondary mb-3">Which account are you reconciling?</p>
      <select class="form-control mx-auto" style="max-width:280px;" v-model="filters.account_id" @change="onAccountChange">
        <option value="" disabled>Select an account…</option>
        <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.name }}</option>
      </select>
    </div>

    <div v-else-if="reconciliationMode && filters.account_id" class="mb-3">
      <reconciliation-panel
        :account-id="filters.account_id"
        @changed="fetchReconciliationSummary(); fetchTransactions();"
      ></reconciliation-panel>
    </div>

    <template v-else>
    <div v-if="loading" class="text-center text-secondary py-5">
      <span class="spinner-border spinner-border-sm mr-2"></span>Loading…
    </div>

    <div v-else class="table-responsive bg-white border" style="border-radius:10px;">
      <table class="table mb-0">
        <thead>
          <tr class="text-secondary" style="font-size:12px;">
            <th>Date</th>
            <th>Description</th>
            <th>Account</th>
            <th>Category</th>
            <th class="text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="transactions.length === 0">
            <td colspan="5" class="text-center text-secondary py-4">No transactions yet.</td>
          </tr>
          <tr
            v-for="t in transactions"
            :key="t.id"
            :class="{ 'ov-row-clickable': t.link }"
            @click="t.link && openSource(t)"
          >
            <td>{{ formatDate(t.date) }}</td>
            <td class="text-dark font-weight-bold">
              {{ t.description }}
              <i v-if="t.link" class="bi bi-box-arrow-up-right text-secondary ml-1" style="font-size:11px;" title="Open"></i>
            </td>
            <td>{{ t.account || '—' }}</td>
            <td class="text-secondary">
              {{ t.category || '—' }}
              <span
                v-if="t.verification"
                class="badge badge-success ml-1"
                style="font-size:10px;"
                :title="'Verified via ' + t.verification.method.toUpperCase() + (t.verification.transaction_code ? ' — ' + t.verification.transaction_code : '')"
              ><i class="bi bi-patch-check-fill"></i> Verified</span>
            </td>
            <td class="text-right font-weight-bold" :class="t.amount < 0 ? 'text-danger' : 'text-dark'">
              {{ formatCurrency(t.amount) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="pagination.last_page > 1" class="d-flex justify-content-between align-items-center mt-3">
      <button class="btn btn-sm btn-light" :disabled="pagination.current_page <= 1" @click="goToPage(pagination.current_page - 1)">Previous</button>
      <span class="text-secondary" style="font-size:12px;">Page {{ pagination.current_page }} of {{ pagination.last_page }}</span>
      <button class="btn btn-sm btn-light" :disabled="pagination.current_page >= pagination.last_page" @click="goToPage(pagination.current_page + 1)">Next</button>
    </div>
    </template>

    <!-- Source detail modal -->
    <b-modal v-model="sourceModal.open" :title="sourceModalTitle" hide-footer size="lg">
      <div v-if="sourceModal.loading" class="text-center text-secondary py-4">
        <span class="spinner-border spinner-border-sm mr-2"></span>Loading…
      </div>
      <div v-else-if="sourceModal.error" class="alert alert-danger py-2" style="font-size:13px;">{{ sourceModal.error }}</div>
      <div v-else-if="sourceModal.data">
        <div v-if="sourceFullLink" class="mb-2 text-right">
          <a :href="sourceFullLink" class="text-primary" style="font-size:12px;font-weight:600;">
            Open full {{ sourceModalTitle.toLowerCase() }} <i class="bi bi-box-arrow-up-right ml-1"></i>
          </a>
        </div>
        <table class="table table-sm mb-0">
          <tbody>
            <tr v-for="f in sourceFields" :key="f.label">
              <td class="text-secondary" style="width:40%;">{{ f.label }}</td>
              <td class="text-dark font-weight-bold">{{ f.value }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="sourceModal.verification" class="mt-3 p-3" style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;">
          <div class="font-weight-bold text-success mb-2"><i class="bi bi-patch-check-fill mr-1"></i>Verified via {{ sourceModal.verification.method.toUpperCase() }}</div>
          <div style="font-size:13px;" class="text-dark">
            <div v-if="sourceModal.verification.provider">Provider: {{ sourceModal.verification.provider }}</div>
            <div v-if="sourceModal.verification.transaction_code">Transaction code: {{ sourceModal.verification.transaction_code }}</div>
            <div v-if="sourceModal.verification.phone">Phone: {{ sourceModal.verification.phone }}</div>
            <div v-if="sourceModal.verification.customer">Customer: {{ sourceModal.verification.customer }}</div>
            <div v-if="sourceModal.verification.amount">Amount: {{ formatCurrency(sourceModal.verification.amount) }}</div>
            <div v-if="sourceModal.verification.validated_at">Validated at: {{ sourceModal.verification.validated_at }}</div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Add transaction modal -->
    <b-modal v-model="showModal" :title="modalTitle" hide-footer no-close-on-backdrop>
      <div v-if="formError" class="alert alert-danger py-2" style="font-size:13px;">{{ formError }}</div>

      <div class="form-row">
        <div class="form-group col-md-6">
          <label>Date</label>
          <date-range-picker :auto-apply="true" :single-date-picker="true" v-model="form.datePicker" class="w-100" />
        </div>
        <div class="form-group col-md-6">
          <label>Description</label>
          <input type="text" class="form-control" placeholder="Write a description" v-model="form.description">
        </div>
      </div>

      <template v-if="modalType === 'journal'">
        <div class="form-row">
          <div class="form-group col-md-6">
            <label>Debit account</label>
            <select class="form-control" v-model="form.debit_account_id">
              <option value="" disabled>Select account</option>
              <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
          </div>
          <div class="form-group col-md-6">
            <label>Credit account</label>
            <select class="form-control" v-model="form.credit_account_id">
              <option value="" disabled>Select account</option>
              <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
          </div>
        </div>
        <div class="form-group">
          <label>Amount (KES)</label>
          <input type="number" min="0.01" step="0.01" class="form-control" v-model="form.amount">
        </div>
      </template>

      <template v-else>
        <div class="form-row">
          <div class="form-group col-md-6">
            <label>Account</label>
            <select class="form-control" v-model="form.account_id">
              <option value="" disabled>Select account</option>
              <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
          </div>
          <div class="form-group col-md-6">
            <label>Amount (KES)</label>
            <input type="number" min="0.01" step="0.01" class="form-control" v-model="form.amount">
          </div>
        </div>

        <div class="form-group">
          <a href="#" class="small" @click.prevent="showAssign = !showAssign">
            {{ showAssign ? 'Remove assignment' : 'Assign to an invoice, order, subscription or reservation' }}
          </a>
        </div>

        <template v-if="showAssign">
          <div class="form-row">
            <div class="form-group col-md-4">
              <label>Type</label>
              <select class="form-control" v-model="assignType" @change="searchPayables('')">
                <option value="invoice">Invoice</option>
                <option value="order">Order</option>
                <option value="subscription">Subscription</option>
                <option value="reservation">Reservation</option>
              </select>
            </div>
            <div class="form-group col-md-8">
              <label>Search by customer</label>
              <input type="text" class="form-control" placeholder="Type a customer name…" v-model="assignSearch" @input="searchPayables(assignSearch)">
            </div>
          </div>

          <div v-if="payableOptions.length" class="list-group mb-3" style="max-height:160px;overflow-y:auto;">
            <a
              v-for="p in payableOptions"
              :key="p.id"
              href="#"
              class="list-group-item list-group-item-action py-2"
              :class="{ active: form.payable_id === p.id }"
              @click.prevent="selectPayable(p)"
            >
              <div class="d-flex justify-content-between">
                <span>{{ p.label }}<span v-if="p.customer" class="text-muted"> — {{ p.customer }}</span></span>
                <span v-if="p.balance !== null" class="text-muted">Bal: {{ formatCurrency(p.balance) }}</span>
              </div>
            </a>
          </div>
        </template>
      </template>

      <div class="form-group">
        <label>Notes</label>
        <textarea class="form-control" rows="3" placeholder="Write a note here…" v-model="form.notes"></textarea>
      </div>

      <div class="form-group">
        <label>Receipt</label>
        <fileuploader description="Files must be 6MB or smaller, and in one of these formats: JPG, JPEG, GIF, TIFF, TIF, BMP, PNG, PDF, or HEIC"></fileuploader>
      </div>

      <div class="d-flex justify-content-end mt-3" style="gap:8px;">
        <button class="btn btn-light" @click="showModal = false">Cancel</button>
        <button class="btn btn-primary" :disabled="saving" @click="submit">
          <span v-if="saving"><span class="spinner-border spinner-border-sm mr-1"></span>Saving…</span>
          <span v-else>Save</span>
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
import ReconciliationPanel from './ReconciliationPanel.vue';

export default {
  name: 'LedgerTransactions',

  components: { DateRangePicker, ReconciliationPanel },

  data() {
    return {
      loading: true,
      saving: false,
      transactions: [],
      accounts: [],
      pagination: { current_page: 1, last_page: 1 },
      filters: { account_id: '', search: '', start_date: '', end_date: '' },
      dateRange: { startDate: null, endDate: null },
      reconciliationMode: false,
      reconciliation: null,

      sourceModal: { open: false, loading: false, error: '', type: '', link: null, data: null, verification: null },

      showModal: false,
      modalType: 'deposit',
      formError: '',
      form: {
        datePicker: { startDate: new Date() },
        description: '',
        account_id: '',
        amount: '',
        debit_account_id: '',
        credit_account_id: '',
        payable_type: '',
        payable_id: '',
        notes: '',
      },

      showAssign: false,
      assignType: 'invoice',
      assignSearch: '',
      payableOptions: [],
      searchDebounce: null,
    };
  },

  computed: {
    modalTitle() {
      return { deposit: 'Add deposit', withdrawal: 'Add withdrawal', journal: 'Add journal entry' }[this.modalType];
    },
    selectedAccount() {
      return this.accounts.find(a => a.id === this.filters.account_id) || null;
    },
    sourceModalTitle() {
      const labels = { expense: 'Expense', order: 'Order', subscription: 'Subscription', reservation: 'Reservation', invoice: 'Invoice', purchase: 'Purchase' };
      return labels[this.sourceModal.type] || 'Details';
    },
    // Where "Open full page" goes — each of these pages can now open one
    // specific record directly via a query param it reads on load.
    sourceFullLink() {
      const link = this.sourceModal.link;
      if (!link) return null;
      const routes = {
        order: `/orders?order-number=${encodeURIComponent(link.id)}`,
        invoice: `/invoices/${link.id}`,
        expense: `/expenses?expense-id=${encodeURIComponent(link.id)}`,
        subscription: `/subscriptions?subscription-id=${encodeURIComponent(link.id)}`,
        reservation: `/reservations?reservation-id=${encodeURIComponent(link.id)}`,
        purchase: `/purchases?purchase-id=${encodeURIComponent(link.id)}`,
      };
      return routes[link.type] || null;
    },
    sourceFields() {
      const d = this.sourceModal.data;
      if (!d) return [];
      const money = v => this.formatCurrency(v);

      switch (this.sourceModal.type) {
        case 'expense':
          return [
            { label: 'Expense', value: d.expense || '—' },
            { label: 'Category', value: d.ExpenseCategory?.name || '—' },
            { label: 'Amount', value: money(d.amount) },
            { label: 'Payment method', value: d.paymentMethod?.payment_method || '—' },
            { label: 'Date', value: this.formatDate(d.created_at) },
          ];
        case 'order':
          return [
            { label: 'Order #', value: d.order || '—' },
            { label: 'Total', value: money(d.total_amount) },
            { label: 'Balance', value: money(d.balance) },
            { label: 'Status', value: d.status ? 'Held' : 'Completed' },
            { label: 'Date', value: this.formatDate(d.created_at) },
          ];
        case 'subscription':
          return [
            { label: 'Subscription #', value: d.subscription_order || '—' },
            { label: 'Customer', value: d.customer?.full_name || '—' },
            { label: 'Package', value: d.package?.name || '—' },
            { label: 'Amount due', value: money(d.amount_due) },
            { label: 'Status', value: d.status || '—' },
            { label: 'Period', value: `${this.formatDate(d.start_date)} – ${this.formatDate(d.end_date)}` },
          ];
        case 'reservation':
          return [
            { label: 'Reservation #', value: d.reservation_number || '—' },
            { label: 'Room', value: d.product?.product || '—' },
            { label: 'Total', value: money(d.total_amount) },
            { label: 'Status', value: d.status || '—' },
            { label: 'Check-in', value: this.formatDate(d.check_in) },
            { label: 'Check-out', value: this.formatDate(d.check_out) },
          ];
        case 'invoice':
          return [
            { label: 'Invoice #', value: d.invoice_number || '—' },
            { label: 'Customer', value: d.customer?.full_name || '—' },
            { label: 'Total', value: money(d.total) },
            { label: 'Balance', value: money(d.balance) },
            { label: 'Status', value: d.status || '—' },
          ];
        case 'purchase':
          return [
            { label: 'Purchase order', value: d.purchase_order || '—' },
            { label: 'Total', value: money(d.total_amount) },
            { label: 'Amount paid', value: money(d.amount_paid) },
            { label: 'Date', value: this.formatDate(d.created_at) },
          ];
        default:
          return [];
      }
    },
  },

  mounted() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('account_id')) {
      this.filters.account_id = params.get('account_id');
    }
    this.fetchAccounts();
    this.fetchTransactions();
    if (this.filters.account_id) this.fetchReconciliationSummary();
  },

  methods: {
    formatCurrency(v) {
      return 'KES ' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    },
    fmt(date, format) {
      return date ? moment(date).format(format) : '';
    },

    onDateChange() {
      this.filters.start_date = this.dateRange.startDate ? moment(this.dateRange.startDate).format('YYYY-MM-DD') : '';
      this.filters.end_date = this.dateRange.endDate ? moment(this.dateRange.endDate).format('YYYY-MM-DD') : '';
      this.fetchTransactions();
    },

    async openSource(t) {
      this.sourceModal = { open: true, loading: true, error: '', type: t.link.type, link: t.link, data: null, verification: t.verification };

      const endpoints = {
        expense: `/api/expense/${t.link.id}`,
        order: `/api/order-details-minimal?orderId=${t.link.id}`,
        subscription: `/api/subscriptions/${t.link.id}`,
        reservation: `/api/reservations/${t.link.id}`,
        invoice: `/api/invoices/${t.link.id}`,
        purchase: `/api/purchases/${t.link.id}`,
      };
      const url = endpoints[t.link.type];
      if (!url) {
        this.sourceModal.loading = false;
        this.sourceModal.error = 'Nothing to open for this transaction.';
        return;
      }

      try {
        const { data } = await axios.get(url);
        // Reservation's `show` wraps the record in { status, data }; every
        // other endpoint returns the record directly.
        this.sourceModal.data = data.data || data;
      } catch (err) {
        this.sourceModal.error = 'Could not load this record.';
      } finally {
        this.sourceModal.loading = false;
      }
    },

    async fetchAccounts() {
      try {
        const { data } = await axios.get('/api/ledger/accounts');
        this.accounts = data;
      } catch (_) {}
    },

    onAccountChange() {
      this.reconciliation = null;
      if (this.filters.account_id) this.fetchReconciliationSummary();
      this.fetchTransactions();
    },

    async fetchReconciliationSummary() {
      if (!this.filters.account_id) return;
      try {
        const { data } = await axios.get(`/api/ledger/accounts/${this.filters.account_id}/reconciliation`);
        this.reconciliation = data;
      } catch (_) {
        this.reconciliation = null;
      }
    },

    async fetchTransactions() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/ledger/transactions', { params: this.queryParams() });
        this.transactions = data.data;
        this.pagination = { current_page: data.current_page, last_page: data.last_page };
      } catch (_) {
        this.transactions = [];
      } finally {
        this.loading = false;
      }
    },

    async goToPage(page) {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/ledger/transactions', { params: this.queryParams(page) });
        this.transactions = data.data;
        this.pagination = { current_page: data.current_page, last_page: data.last_page };
      } finally {
        this.loading = false;
      }
    },

    queryParams(page) {
      return {
        account_id: this.filters.account_id || undefined,
        search: this.filters.search || undefined,
        start_date: this.filters.start_date || undefined,
        end_date: this.filters.end_date || undefined,
        page,
      };
    },

    openModal(type) {
      this.modalType = type;
      this.formError = '';
      this.showAssign = false;
      this.payableOptions = [];
      this.form = {
        datePicker: { startDate: new Date() },
        description: '',
        account_id: '',
        amount: '',
        debit_account_id: '',
        credit_account_id: '',
        payable_type: '',
        payable_id: '',
        notes: '',
      };
      this.showModal = true;
    },

    searchPayables(term) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(async () => {
        try {
          const { data } = await axios.get('/api/ledger/payables', { params: { type: this.assignType, q: term || undefined } });
          this.payableOptions = data;
        } catch (_) {
          this.payableOptions = [];
        }
      }, 250);
    },

    selectPayable(p) {
      this.form.payable_type = p.type;
      this.form.payable_id   = p.id;
    },

    async submit() {
      this.formError = '';
      if (!this.form.amount || Number(this.form.amount) <= 0) {
        this.formError = 'Enter an amount greater than zero.';
        return;
      }
      if (this.modalType === 'journal' && (!this.form.debit_account_id || !this.form.credit_account_id)) {
        this.formError = 'Select both a debit and a credit account.';
        return;
      }
      if (this.modalType !== 'journal' && !this.form.account_id) {
        this.formError = 'Select an account.';
        return;
      }

      this.saving = true;
      try {
        const receiptEl = document.getElementById('filename');
        await axios.post('/api/ledger/transactions', {
          type: this.modalType,
          date: moment(this.form.datePicker.startDate).format('YYYY-MM-DD'),
          description: this.form.description,
          account_id: this.form.account_id,
          amount: this.form.amount,
          debit_account_id: this.form.debit_account_id,
          credit_account_id: this.form.credit_account_id,
          payable_type: this.form.payable_type,
          payable_id: this.form.payable_id,
          notes: this.form.notes,
          receipt_path: receiptEl ? receiptEl.value : '',
        });
        this.showModal = false;
        this.$toasts.push({ type: 'success', message: 'Transaction saved.' });
        this.fetchTransactions();
      } catch (err) {
        this.formError = err.response?.data?.message || 'Could not save this transaction.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.ov-row-clickable {
  cursor: pointer;
}
.ov-row-clickable:hover {
  background: #f8f9fa;
}
.ov-switch {
  width: 38px;
  height: 20px;
  border-radius: 10px;
  background: #ced4da;
  position: relative;
  cursor: pointer;
  transition: background .15s;
  flex-shrink: 0;
}
.ov-switch::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: left .15s;
}
.ov-switch--on {
  background: #16a34a;
}
.ov-switch--on::after {
  left: 20px;
}
</style>
